import { useMemo, useState } from 'react';
import { TabelaCartoes } from '@/components/common/TabelaCartoes';
import {
  Download,
  Eye,
  X,
  AlertTriangle,
  Inbox,
  ExternalLink,
  Phone,
  MessageCircle,
  Mail,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TableSkeleton } from '@/components/common/Skeletons';
import { Skeleton } from '@/components/ui/skeleton';
import { AvatarIniciais } from '@/components/common/ListaMobile';
import { getAvatarColor, getUserInitials } from '@/components/common/avatarHelpers';
import { cn } from '@/lib/utils';
import { formatDateTime, formatPhoneNumber } from '@/utils/formatters';
import { useJobs, useApplications, useUpdateApplicationStatus } from '@/hooks/useCareers';
import { careersApi } from '@/api/endpoints/careers';
import type {
  ApplicationFilters,
  ApplicationStatus,
  JobApplication,
} from '@/api/endpoints/careers';
import { digitosTelefone } from '@/utils/telefone';

const STATUS_OPTIONS: { value: ApplicationStatus; label: string }[] = [
  { value: 'novo', label: 'Novo' },
  { value: 'em_analise', label: 'Em análise' },
  { value: 'contatado', label: 'Contatado' },
  { value: 'arquivado', label: 'Arquivado' },
];

const STATUS_CLASS: Record<ApplicationStatus, string> = {
  novo: 'bg-info/10 text-info',
  em_analise: 'bg-warning/10 text-warning',
  contatado: 'bg-online/10 text-online',
  arquivado: 'bg-muted text-text-secondary',
};

const inputClass =
  'rounded-lg border border-border bg-card px-3 py-2 text-sm text-text-body transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';

function originLabel(app: JobApplication): string {
  return app.tipo === 'banco' ? 'Banco de talentos' : (app.vagaTitulo ?? 'Vaga removida');
}

export function ApplicationsTab() {
  const { data: jobs } = useJobs();
  const [statusFilter, setStatusFilter] = useState<'all' | ApplicationStatus>('all');
  // Origem: todas / só banco de talentos (tipo=banco) / vaga específica (vagaId).
  const [originFilter, setOriginFilter] = useState<'all' | 'banco' | number>('all');
  const [detail, setDetail] = useState<JobApplication | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const filters = useMemo<ApplicationFilters>(() => {
    const f: ApplicationFilters = {};
    if (statusFilter !== 'all') f.status = statusFilter;
    if (originFilter === 'banco') f.tipo = 'banco';
    else if (typeof originFilter === 'number') f.vagaId = originFilter;
    return f;
  }, [statusFilter, originFilter]);

  const { data: applications, isLoading, error } = useApplications(filters);
  const updateStatusMutation = useUpdateApplicationStatus();

  const handleStatusChange = async (app: JobApplication, status: ApplicationStatus) => {
    try {
      await updateStatusMutation.mutateAsync({ id: app.id, status });
    } catch (err) {
      console.error('Erro ao atualizar status da candidatura:', err);
      alert('Erro ao atualizar o status. Tente novamente.');
    }
  };

  const handleDownloadCv = async (app: JobApplication) => {
    setDownloadingId(app.id);
    try {
      const url = await careersApi.getApplicationCvUrl(app.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Erro ao baixar CV:', err);
      alert('Erro ao gerar o link do currículo. Tente novamente.');
    } finally {
      setDownloadingId(null);
    }
  };

  const list = applications ?? [];

  if (error) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-error/10">
            <AlertTriangle size={26} className="text-error" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-text-title">
            Erro ao carregar candidaturas
          </h2>
          <p className="text-sm text-text-secondary">
            Não foi possível carregar a lista de candidaturas.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | ApplicationStatus)}
          className={inputClass}
        >
          <option value="all">Todos os status</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          value={originFilter}
          onChange={(e) => {
            const v = e.target.value;
            setOriginFilter(v === 'all' ? 'all' : v === 'banco' ? 'banco' : Number(v));
          }}
          className={inputClass}
        >
          <option value="all">Todas as vagas</option>
          <option value="banco">Banco de talentos</option>
          {(jobs ?? []).map((job) => (
            <option key={job.id} value={job.id}>
              {job.titulo}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <>
          <div className="flex flex-col gap-3 lg:hidden">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-card"
              >
                <div className="flex items-start gap-3">
                  <Skeleton className="size-10 shrink-0 rounded-full" />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-3 w-2/3" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
                <Skeleton className="h-10 w-full rounded-[10px]" />
              </div>
            ))}
          </div>
          <div className="hidden w-full overflow-hidden rounded-xl border border-border bg-card shadow-card animate-fade-up lg:block">
            <div className="overflow-x-auto">
              <TabelaCartoes className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  <tr>
                    <th className="px-6 py-3">Nome</th>
                    <th className="px-6 py-3">Contato</th>
                    <th className="px-6 py-3">Área</th>
                    <th className="px-6 py-3">Origem</th>
                    <th className="px-6 py-3">Data</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Ações</th>
                  </tr>
                </thead>
              </TabelaCartoes>
              <TableSkeleton
                rows={4}
                columns={['w-32', 'w-28', 'w-20', 'w-24', 'w-20', 'w-24', 'w-16']}
              />
            </div>
          </div>
        </>
      ) : list.length > 0 ? (
        <>
          {/* Celular e tablet: um cartão por pessoa, com contato em um toque */}
          <div className="flex flex-col gap-3 lg:hidden">
            {list.map((app) => (
              <CandidaturaCartao
                key={app.id}
                app={app}
                onDetails={() => setDetail(app)}
                onStatusChange={(status) => handleStatusChange(app, status)}
                statusPending={updateStatusMutation.isPending}
                onDownloadCv={() => handleDownloadCv(app)}
                downloading={downloadingId === app.id}
              />
            ))}
          </div>
          <div className="hidden w-full overflow-hidden rounded-xl border border-border bg-card shadow-card animate-fade-up lg:block">
            <div className="overflow-x-auto">
              <TabelaCartoes className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  <tr>
                    <th className="px-6 py-3">Nome</th>
                    <th className="px-6 py-3">Contato</th>
                    <th className="px-6 py-3">Área</th>
                    <th className="px-6 py-3">Origem</th>
                    <th className="px-6 py-3">Data</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {list.map((app) => (
                    <tr key={app.id} className="hover:bg-muted/30">
                      <td className="px-6 py-4">
                        <span className="font-medium text-text-title">{app.nome}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="block text-text-body">
                          {formatPhoneNumber(app.whatsapp)}
                        </span>
                        <span className="block text-xs text-text-hint">{app.email}</span>
                      </td>
                      <td className="px-6 py-4 text-text-body">{app.area}</td>
                      <td className="px-6 py-4 text-text-body">{originLabel(app)}</td>
                      <td className="px-6 py-4 text-text-secondary">
                        {formatDateTime(app.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={app.status}
                          onChange={(e) =>
                            handleStatusChange(app, e.target.value as ApplicationStatus)
                          }
                          disabled={updateStatusMutation.isPending}
                          className={`rounded-full border-0 px-2.5 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 ${STATUS_CLASS[app.status]}`}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setDetail(app)}
                            className="text-xs"
                          >
                            <Eye size={14} />
                            Detalhes
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleDownloadCv(app)}
                            disabled={downloadingId === app.id}
                            className="text-xs"
                          >
                            <Download size={14} />
                            {downloadingId === app.id ? 'Abrindo...' : 'Baixar CV'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TabelaCartoes>
            </div>
          </div>
        </>
      ) : (
        <div className="w-full overflow-hidden rounded-xl border border-border bg-card shadow-card animate-fade-up">
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-primary/10">
                <Inbox size={26} className="text-primary" />
              </div>
              <p className="mb-1 text-lg font-medium text-text-title">
                Nenhuma candidatura encontrada
              </p>
              <p className="text-sm text-text-secondary">
                Ajuste os filtros para ver mais resultados.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Detalhe da candidatura */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm max-md:items-end max-md:p-0">
          <div className="w-full max-w-lg rounded-xl border border-border bg-card shadow-elevated max-h-[90vh] overflow-y-auto animate-fade-up max-md:max-h-[92dvh] max-md:rounded-b-none max-md:border-x-0 max-md:border-b-0 max-md:pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-between border-b border-border p-6">
              <h2 className="text-xl font-semibold text-text-title">{detail.nome}</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDetail(null)}
                aria-label="Fechar"
                className="rounded-full"
              >
                <X size={20} />
              </Button>
            </div>

            <div className="space-y-4 p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-text-hint">
                    WhatsApp
                  </p>
                  <p className="text-sm text-text-body">{formatPhoneNumber(detail.whatsapp)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-text-hint">
                    E-mail
                  </p>
                  <p className="text-sm text-text-body">{detail.email}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-text-hint">Área</p>
                  <p className="text-sm text-text-body">{detail.area}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-text-hint">
                    Origem
                  </p>
                  <p className="text-sm text-text-body">{originLabel(detail)}</p>
                </div>
              </div>

              {detail.linkedin && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-hint">
                    LinkedIn
                  </p>
                  <a
                    href={detail.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    {detail.linkedin}
                    <ExternalLink size={13} />
                  </a>
                </div>
              )}

              {detail.mensagem && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-hint">
                    Mensagem
                  </p>
                  <p className="whitespace-pre-wrap rounded-lg border border-border bg-bg-elevated p-3 text-sm text-text-body">
                    {detail.mensagem}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 border-t border-border bg-bg-elevated p-6">
              <Button variant="secondary" onClick={() => setDetail(null)}>
                Fechar
              </Button>
              <Button
                onClick={() => handleDownloadCv(detail)}
                disabled={downloadingId === detail.id}
              >
                <Download size={16} />
                {downloadingId === detail.id ? 'Abrindo...' : 'Baixar CV'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// "Recebida hoje, 09:00", "Recebida ontem, 17:00" ou "Recebida em 14/10".
function recebidaEm(data: string): string {
  const d = new Date(data);
  if (isNaN(d.getTime())) return '';
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const hoje = new Date();
  const ontem = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1);
  if (d.toDateString() === hoje.toDateString()) return `Recebida hoje, ${hora}`;
  if (d.toDateString() === ontem.toDateString()) return `Recebida ontem, ${hora}`;
  const opcoes: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit' };
  if (d.getFullYear() !== hoje.getFullYear()) opcoes.year = 'numeric';
  return `Recebida em ${d.toLocaleDateString('pt-BR', opcoes)}`;
}

const botaoContato =
  'flex h-10 items-center justify-center gap-1 rounded-[10px] border border-border text-text-secondary transition-colors active:bg-muted';

// Cartão da candidatura no celular e no tablet: tocar na pessoa abre os
// detalhes; o status troca no mesmo select da tabela, em tamanho de pílula; e
// a base tem os 4 atalhos (ligar, WhatsApp, e-mail e currículo).
function CandidaturaCartao({
  app,
  onDetails,
  onStatusChange,
  statusPending,
  onDownloadCv,
  downloading,
}: {
  app: JobApplication;
  onDetails: () => void;
  onStatusChange: (status: ApplicationStatus) => void;
  statusPending: boolean;
  onDownloadCv: () => void;
  downloading: boolean;
}) {
  const telefone = digitosTelefone(app.whatsapp);

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onDetails}
          aria-label={`Ver detalhes de ${app.nome}`}
          className="flex min-w-0 flex-1 items-start gap-3 text-left"
        >
          <AvatarIniciais
            iniciais={getUserInitials(app.nome)}
            className={cn('size-10 shrink-0', getAvatarColor(String(app.id)))}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[15px] font-semibold leading-snug text-text-title">
              {app.nome}
            </span>
            <span className="truncate text-[13px] text-text-secondary">{originLabel(app)}</span>
            <span className="truncate text-xs text-text-hint">{recebidaEm(app.createdAt)}</span>
          </span>
        </button>
        <select
          value={app.status}
          onChange={(e) => onStatusChange(e.target.value as ApplicationStatus)}
          disabled={statusPending}
          aria-label={`Status da candidatura de ${app.nome}`}
          className={`shrink-0 rounded-full border-0 px-2 py-1 text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30 ${STATUS_CLASS[app.status]}`}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {telefone ? (
          <>
            <a
              href={`tel:+${telefone}`}
              aria-label={`Ligar para ${app.nome}`}
              className={botaoContato}
            >
              <Phone size={17} aria-hidden="true" />
            </a>
            <a
              href={`https://wa.me/${telefone}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Abrir WhatsApp de ${app.nome}`}
              className={botaoContato}
            >
              <MessageCircle size={17} aria-hidden="true" />
            </a>
          </>
        ) : (
          <>
            <span aria-hidden="true" className={cn(botaoContato, 'opacity-40')}>
              <Phone size={17} />
            </span>
            <span aria-hidden="true" className={cn(botaoContato, 'opacity-40')}>
              <MessageCircle size={17} />
            </span>
          </>
        )}
        {app.email ? (
          <a
            href={`mailto:${app.email}`}
            aria-label={`Enviar e-mail para ${app.nome}`}
            className={botaoContato}
          >
            <Mail size={17} aria-hidden="true" />
          </a>
        ) : (
          <span aria-hidden="true" className={cn(botaoContato, 'opacity-40')}>
            <Mail size={17} />
          </span>
        )}
        <button
          type="button"
          onClick={onDownloadCv}
          disabled={downloading}
          aria-label={`Baixar currículo de ${app.nome}`}
          className={cn(
            botaoContato,
            'border-transparent bg-primary/15 text-xs font-semibold text-primary disabled:opacity-50'
          )}
        >
          <Download size={15} aria-hidden="true" />
          {downloading ? '...' : 'CV'}
        </button>
      </div>
    </div>
  );
}
